import React from 'react';
import styled from 'styled-components';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router';
import { Header } from '@app/navigation/header';
import { Body, Body1, Body5, Container, Page, VisuallyHidden } from '@ui';
import { BackArrowButton, FlatButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { AI_TOOLS } from '@constants/ai';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
import { fetchScoreSpecs, SCOPE_OPTIONS, UNIVERSE_KINDS } from '@services/scoreBackfills';
import { listSpecs } from '@services/scoreSpecs';
import { sortSpecsByRecency, updatedAtById } from '@features/scoreBackfill/specOrder';
import { readSpecParam, resolveSpecParam } from '@features/scoreBackfill/specSearch';
import { isLegacyWindow, offeredMarketCapVersions, resolveMarketCapVersion } from '@features/scoreBackfill/format';
import { ScoreBackfillForm } from '@features/scoreBackfill/ScoreBackfillForm';
import { BackfillProgress } from '@features/scoreBackfill/BackfillProgress';
import { BackfillHistory } from '@features/scoreBackfill/BackfillHistory';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const HEADING_ID = 'score-backfill-heading';

// Progress sub-route: reads :jobId from the URL and self-hydrates when opened via a deep
// link / refresh (initialJob null). openedJobRef carries the job when opened from History
// so Progress can render immediately without a fetch.
const ProgressRoute = ({ openedJobRef, onViewHistory, onStartSimilar }) => {
  const { jobId } = useParams();
  const opened = openedJobRef.current;
  // job_id arrives from the API as a number; useParams().jobId is always a string — coerce both.
  const initialJob = opened && String(opened.job_id) === String(jobId) ? opened : null;

  return (
    <BackfillProgress
      key={jobId}
      jobId={jobId}
      initialJob={initialJob}
      onViewHistory={onViewHistory}
      onStartSimilar={onStartSimilar}
    />
  );
};

export const ScoreBackfill = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tools = useCurrentUserToolsAvailable();

  const [specsLoading, setSpecsLoading] = React.useState(true);
  const [specs, setSpecs] = React.useState(null);
  const [universeOptions, setUniverseOptions] = React.useState(null);
  const [specsError, setSpecsError] = React.useState(null);

  // #8 — the form fields are owned here so they survive navigating to History / Progress and
  // back. ScoreBackfillForm is fully controlled off this state. windowSeededFromLegacy is not a
  // request field: it marks dates carried over by startSimilar from a job whose window bounded a
  // different column, and the form clears it on the first edit to either date.
  const [formState, setFormState] = React.useState({
    specId: '',
    universeKind: UNIVERSE_KINDS.WHOLE_CORPUS,
    indexValue: '',
    tickerTags: [],
    fromDate: null,
    toDate: null,
    scope: SCOPE_OPTIONS.FULL_HISTORY,
    // null means "not chosen": the request omits market_cap_version, and omission is documented as
    // unchanged behavior, which is v1. The value only ever becomes a string by the analyst picking
    // from the server's own advertised list (or startSimilar reproducing a job's), so this client
    // structurally cannot send a version a backend predating #523 never advertised.
    marketCapVersion: null,
    windowSeededFromLegacy: false,
  });

  // Job passed when opening from History so Progress gets an initialJob; a deep link /
  // refresh leaves this null and Progress self-hydrates from :jobId.
  const openedJobRef = React.useRef(null);

  // Client-side hardening — the route is otherwise reachable by direct URL.
  React.useEffect(() => {
    if (tools && tools.length > 0 && !tools.includes(AI_TOOLS.SCORE_BACKFILL)) {
      navigate(ROUTES.home);
    }
  }, [tools, navigate]);

  // The picker payload carries no timestamp, so "most recently edited first" has to be joined on
  // from the score-spec list. That route is gated on its own grant, and a 403 there does not just
  // fail — handle400Statuses would navigate this operator off the page they are working on. So the
  // call is made ONLY when the grant is already in hand, never attempted-and-caught.
  const canReadSpecRecords = !!tools && tools.includes(AI_TOOLS.SCORE_SPEC_ADMIN);

  const loadSpecs = React.useCallback(async () => {
    setSpecsLoading(true);
    setSpecsError(null);
    const { specs: specList, universeOptions: options, error } = await fetchScoreSpecs();

    // Ordering is a nicety; the picker is the point. A failed or skipped recency read leaves the
    // list in the server's alphabetical order and says nothing — surfacing an error here would
    // report a broken screen when the only thing missing is a sort.
    let updatedById = null;
    if (canReadSpecRecords && specList && specList.length > 0) {
      const { specs: records } = await listSpecs();
      updatedById = updatedAtById(records);
    }

    setSpecs(sortSpecsByRecency(specList, updatedById));
    setUniverseOptions(options);
    setSpecsError(error);
    setSpecsLoading(false);
  }, [canReadSpecRecords]);

  React.useEffect(() => {
    loadSpecs();
  }, [loadSpecs]);

  // universeOptions loads async — once it says whole_corpus is disallowed, drop the default
  // kind to index (only while the analyst hasn't moved off the default).
  React.useEffect(() => {
    if (universeOptions && universeOptions.whole_corpus_allowed === false) {
      setFormState((prev) =>
        prev.universeKind === UNIVERSE_KINDS.WHOLE_CORPUS ? { ...prev, universeKind: UNIVERSE_KINDS.INDEX } : prev
      );
    }
  }, [universeOptions]);

  // universeOptions loads async. Unlike the whole_corpus guard above, this does NOT check "the
  // analyst hasn't moved off the default" — membership in the offered list is the whole test, so a
  // deliberate choice survives as long as it is still on offer and is corrected the moment it isn't.
  // It corrects to null (omit), never to a guess at another version.
  React.useEffect(() => {
    const versions = offeredMarketCapVersions(universeOptions);
    setFormState((prev) => {
      const next = resolveMarketCapVersion(versions, prev.marketCapVersion);
      return next === prev.marketCapVersion ? prev : { ...prev, marketCapVersion: next };
    });
  }, [universeOptions]);

  const { pathname, search } = location;

  // ?spec=<id or search text> pre-selects a spec: the way another screen's "Run a backfill" hands an
  // analyst here with the spec already chosen (scoreBackfillPathForSpec). It can only be honoured
  // once the list is in, so it waits on `specs` rather than running at mount, and it is applied once
  // per mount — a spec the analyst then clears stays cleared, and the form's other fields are left
  // alone. A non-empty list is the trigger: a failed load sets an empty one, and a retry that then
  // succeeds must still get its turn. An already-chosen spec is never overwritten.
  const specParamApplied = React.useRef(false);
  React.useEffect(() => {
    if (specParamApplied.current || !specs || specs.length === 0) return;
    specParamApplied.current = true;
    const id = resolveSpecParam(specs, readSpecParam(search));
    if (!id) return;
    setFormState((prev) => (prev.specId ? prev : { ...prev, specId: id }));
  }, [specs, search]);

  const isHistory = pathname.startsWith(ROUTES.scoreBackfillHistory);
  const isJobs = pathname.startsWith(ROUTES.scoreBackfillJobs);
  const isForm = !isHistory && !isJobs;

  const jobIdInPath = isJobs ? pathname.split(`${ROUTES.scoreBackfillJobs}/`)[1] : null;
  const viewAnnouncement = isHistory ? 'History' : isJobs ? `Backfill #${jobIdInPath}` : 'Form';

  // #17 — move focus to the page heading on every route change.
  React.useEffect(() => {
    const el = document.getElementById(HEADING_ID);
    if (el) {
      el.focus();
    }
  }, [pathname]);

  const goToForm = React.useCallback(() => navigate(ROUTES.scoreBackfill), [navigate]);
  const goToHistory = React.useCallback(() => navigate(ROUTES.scoreBackfillHistory), [navigate]);

  const openJob = React.useCallback(
    (job) => {
      openedJobRef.current = job;
      navigate(`${ROUTES.scoreBackfillJobs}/${job.job_id}`);
    },
    [navigate]
  );

  const handleJobCreated = React.useCallback(
    (job) => {
      openedJobRef.current = job;
      navigate(`${ROUTES.scoreBackfillJobs}/${job.job_id}`);
    },
    [navigate]
  );

  // #7 — seed the form from a prior job's params, then return to the form.
  const startSimilar = React.useCallback(
    (job) => {
      const kind = job.universe?.kind;
      // Respect a disallowed whole_corpus — the mount-time guard won't re-run for this re-seed.
      const wholeCorpusDisallowed = universeOptions?.whole_corpus_allowed === false;
      const universeKind =
        (!kind || kind === UNIVERSE_KINDS.WHOLE_CORPUS) && wholeCorpusDisallowed
          ? UNIVERSE_KINDS.INDEX
          : kind || UNIVERSE_KINDS.WHOLE_CORPUS;
      setFormState({
        specId: job.score_spec_id || '',
        universeKind,
        indexValue: kind === UNIVERSE_KINDS.INDEX ? job.universe?.value || '' : '',
        tickerTags: kind === UNIVERSE_KINDS.TICKERS ? (job.universe?.value || []).map((symbol) => ({ symbol })) : [],
        fromDate: job.occasion_from ? new Date(job.occasion_from) : null,
        // A job stamped at scoring time has no occasion_to — its window ended when it was requested —
        // so null is right here; the form derives that mode again from the spec and asks for no end.
        toDate: job.occasion_to ? new Date(job.occasion_to) : null,
        scope: job.scope || SCOPE_OPTIONS.FULL_HISTORY,
        // The version is a run parameter, so a reproduction carries it — not seeding it would
        // silently drop a v2 job's reproduction onto a different band. The RAW field, not
        // marketCapVersionOf: null on the job means it predates the field and ran v1, which is also
        // what null in the form sends, and seeding a literal 'v1' would put a field on the wire the
        // analyst never chose.
        //
        // Clamped inline ONLY once the options are actually in hand, because the two "no list"
        // states are not the same thing. "The server offers nothing" must drop the version; "the
        // discovery call hasn't answered yet" must not — this route is reachable by deep link to
        // /jobs/:id, where Progress renders before that call resolves, so clamping unconditionally
        // would turn a v2 job's reproduction into a v1 run for anyone who clicked promptly, with
        // nothing on screen to say so. While it is still null the raw value rides along and the
        // reconcile effect above clamps it the moment the list lands. Once universeOptions IS set
        // that effect will not fire again on a re-seed, which is why this clamps for itself — the
        // same gap the wholeCorpusDisallowed clamp above exists to cover.
        marketCapVersion: universeOptions
          ? resolveMarketCapVersion(offeredMarketCapVersions(universeOptions), job.market_cap_version)
          : job.market_cap_version || null,
        // A pre-switch job's dates bounded document dates; the new run bounds knowable-at. The
        // dates are still the best seed on offer, but the form says what changed under them.
        windowSeededFromLegacy: isLegacyWindow(job),
      });
      navigate(ROUTES.scoreBackfill);
    },
    [navigate, universeOptions]
  );

  // #16 — context-aware back-arrow: jobs → history, history → form, form → home.
  const onGoBack = () => {
    if (isJobs) {
      navigate(ROUTES.scoreBackfillHistory);
    } else if (isHistory) {
      navigate(ROUTES.scoreBackfill);
    } else {
      navigate(ROUTES.home);
    }
  };
  const backLabel = isJobs ? 'Back to history' : isHistory ? 'Back to form' : 'Back to home';

  // #11 — History earns a wider column; form / progress stay narrow.
  const pageWidth = isHistory ? '1640px' : '800px';

  // The Page clips anything positioned past its bottom edge (its overflow-x: auto makes overflow-y
  // auto too), and with the spec picker alone on the form that edge sits right under the picker —
  // the spec search's results would be cut off. A floor of 80vh leaves room for them to float;
  // it is a minimum, so a full form or a long History still takes whatever height it needs.
  const pageMinHeight = '80vh';

  const renderFormView = () => {
    if (specsLoading) {
      return <Body5 isLowContrast>Loading score specs…</Body5>;
    }

    if (specsError) {
      return (
        <div>
          <Body5>{specsError}</Body5>
          <Container top={12}>
            <FlatButton onClick={loadSpecs}>Retry</FlatButton>
          </Container>
        </div>
      );
    }

    return (
      <ScoreBackfillForm
        specs={specs}
        universeOptions={universeOptions}
        formState={formState}
        onFormChange={setFormState}
        onJobCreated={handleJobCreated}
      />
    );
  };

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <Page width={pageWidth} minHeight={pageMinHeight}>
          <BackArrowButton onClick={onGoBack} aria-label={backLabel} />
          <VisuallyHidden aria-live="polite">{viewAnnouncement}</VisuallyHidden>
          <Container top={48}>
            <Container row spaceBetween verticallyCenter>
              <Body1 bold id={HEADING_ID} tabIndex={-1}>
                Score Backfill
              </Body1>
              <Container row verticallyCenter>
                {!isForm && (
                  <Container right={8}>
                    <SkeletonButton onClick={goToForm}>New backfill</SkeletonButton>
                  </Container>
                )}
                {!isHistory && <TransparentFlatButton onClick={goToHistory}>History</TransparentFlatButton>}
              </Container>
            </Container>

            <Container top={24}>
              <Routes>
                <Route index element={renderFormView()} />
                <Route path="history" element={<BackfillHistory specs={specs} onOpenJob={openJob} />} />
                <Route
                  path="jobs/:jobId"
                  element={
                    <ProgressRoute
                      openedJobRef={openedJobRef}
                      onViewHistory={goToHistory}
                      onStartSimilar={startSimilar}
                    />
                  }
                />
              </Routes>
            </Container>
          </Container>
        </Page>
      </Body>
    </PageWrapper>
  );
};

export default ScoreBackfill;
